import {
  createContext,
  useContext,
  useId,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { ModalShell, type ModalPlacement } from './modal'

interface DrawerContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
  placement: Exclude<ModalPlacement, 'center'>
  titleId: string
  descriptionId: string
}

const DrawerContext = createContext<DrawerContextValue | null>(null)

function useDrawerContext(): DrawerContextValue {
  const context = useContext(DrawerContext)
  if (!context) {
    throw new Error('Drawer components must be used within <Drawer>')
  }
  return context
}

export type DrawerPlacement = Exclude<ModalPlacement, 'center'>

export interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  placement?: DrawerPlacement
  children: ReactNode
}

function Drawer({ open, onOpenChange, placement = 'right', children }: DrawerProps) {
  const titleId = useId()
  const descriptionId = useId()

  return (
    <DrawerContext.Provider value={{ open, onOpenChange, placement, titleId, descriptionId }}>
      {children}
    </DrawerContext.Provider>
  )
}

export interface DrawerTriggerProps {
  children?: ReactNode
  className?: string
}

function DrawerTrigger({ children, className }: DrawerTriggerProps) {
  const { open, onOpenChange } = useDrawerContext()

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => onOpenChange(true)}
      className={cn('inline-flex items-center', className)}
    >
      {children}
    </button>
  )
}

export interface DrawerContentProps extends HTMLAttributes<HTMLDivElement> {
  hideCloseButton?: boolean
}

function DrawerContent({
  className,
  hideCloseButton = false,
  children,
  ...props
}: DrawerContentProps) {
  const { open, onOpenChange, placement, titleId, descriptionId } = useDrawerContext()

  return (
    <ModalShell
      open={open}
      onOpenChange={onOpenChange}
      placement={placement}
      titleId={titleId}
      descriptionId={descriptionId}
      panelClassName={cn(placement === 'top' || placement === 'bottom' ? '' : 'p-6', className)}
      {...props}
    >
      {children}
      {hideCloseButton ? null : (
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close drawer"
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X aria-hidden className="size-4" />
        </button>
      )}
    </ModalShell>
  )
}

function DrawerHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 flex flex-col space-y-1.5', className)} {...props} />
}

function DrawerFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-auto flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  )
}

function DrawerTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  const { titleId } = useDrawerContext()
  return (
    <h2
      id={titleId}
      className={cn('font-display text-lg font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function DrawerDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  const { descriptionId } = useDrawerContext()
  return (
    <p id={descriptionId} className={cn('text-sm text-muted-foreground', className)} {...props} />
  )
}

export {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
}